// packages/chatbot/src/hooks/useVisitorHistory.ts

import { useEffect, useState, useRef, useCallback } from 'react';
import { visitorConversationsService } from '@dopomogai/api-client';

type VisitorHistory = { items: any[]; total: number };

export function useVisitorHistory(params: {
  enabled: boolean;
  apiBaseUrl?: string | null;
  wat?: string | null;
  getVisitorId: () => string | null;
  getWid: (wat?: string | null) => string | null;
}) {
  const { enabled, apiBaseUrl, wat, getVisitorId, getWid } = params;

  const [history, setHistory] = useState<VisitorHistory>({ items: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastKeyRef = useRef<string | null>(null);

  // --- REFINED PLAN ---
  // Add a nonce state to allow programmatic re-fetching.
  const [nonce, setNonce] = useState(0);

  // --- REFINED PLAN ---
  // Expose a refresh function that clears the cache key and bumps the nonce.
  const refresh = useCallback(() => {
    lastKeyRef.current = null;
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    // Do not run until widget session is ready
    if (!enabled || !apiBaseUrl || !wat) return;

    const abort = { cancelled: false };

    const run = async () => {
      const wid = getWid(wat);
      const visitorId = getVisitorId();
      // Add nonce to the request key to ensure it changes on refresh
      const requestKey = [enabled, apiBaseUrl, wat, wid, visitorId, nonce].join('|');
      if (lastKeyRef.current === requestKey) return;
      lastKeyRef.current = requestKey;

      setIsLoading(true);
      setError(null);
      try {
        if (!wid || !visitorId) {
          setHistory({ items: [], total: 0 });
          return;
        }
        const res = await visitorConversationsService.listForVisitor(
          apiBaseUrl,
          wid as any,
          visitorId,
          wat,
          20,
          0
        );
        if (abort.cancelled) return;
        if ((res as any)?.isError) {
          setError((res as any)?.message || 'Failed to load visitor history.');
          setHistory({ items: [], total: 0 });
        } else {
          setHistory(res as any);
        }
      } catch (e: any) {
        if (!abort.cancelled) {
          setError(e?.message || 'Failed to load visitor history.');
          setHistory({ items: [], total: 0 });
        }
      } finally {
        if (!abort.cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      abort.cancelled = true;
    };
  }, [enabled, apiBaseUrl, wat, getWid, getVisitorId, nonce]); // --- REFINED PLAN --- Add nonce to dependency array

  return { history, isLoading, error, refresh }; // --- REFINED PLAN --- Return the refresh function
}